import React, { useEffect, useState, useCallback } from 'react'
import { createClient } from '@connectrpc/connect'
import { RoleService } from '@/gen/catpocs/v1/roles_pb'
import { transport } from '@/lib/api'
import { AuthContext, type User, type ImpersonationInfo } from '@/lib/auth-context'

export type { User, ImpersonationInfo, AuthContextType } from '@/lib/auth-context'

// Create a typed client for auth operations
const roleClient = createClient(RoleService, transport)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [impersonation, setImpersonation] = useState<ImpersonationInfo>({
    isImpersonating: false,
  })

  const refreshAuth = useCallback(async () => {
    try {
      const response = await roleClient.getCurrentUser({})

      if (response.user) {
        setUser({
          id: response.user.id,
          email: response.user.email,
          name: response.user.name,
          groups: [...response.user.groups],
          isSuperAdmin: response.user.isSuperAdmin,
        })

        // Extract impersonation info
        if (response.isImpersonating && response.impersonatedUser) {
          setImpersonation({
            isImpersonating: true,
            originalUser: response.originalUser ? {
              id: response.originalUser.id,
              email: response.originalUser.email,
              name: response.originalUser.name,
            } : undefined,
            impersonatedUser: {
              id: response.impersonatedUser.id,
              email: response.impersonatedUser.email,
              name: response.impersonatedUser.name,
            },
          })
        } else {
          setImpersonation({
            isImpersonating: false,
          })
        }
      } else {
        setUser(null)
        setImpersonation({ isImpersonating: false })
      }
    } catch (error) {
      console.error('Failed to check auth status:', error)
      setUser(null)
      setImpersonation({ isImpersonating: false })
    }
  }, [])

  // Check for existing session on mount
  useEffect(() => {
    const checkAuth = async () => {
      await refreshAuth()
      setIsLoading(false)
    }

    checkAuth()
  }, [refreshAuth])

  const login = useCallback(() => {
    // Redirect to backend login endpoint (OAuth flow, not gRPC)
    window.location.href = '/auth/login'
  }, [])

  const logout = useCallback(() => {
    // Redirect to backend logout endpoint (session cleanup, not gRPC)
    window.location.href = '/auth/logout'
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        impersonation,
        login,
        logout,
        refreshAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}
